import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormApi, useFormState } from 'informed';
import { addDays, startOfDay } from 'date-fns';
import { components } from 'react-select';
import api from 'api';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { availableCustomAttributesByType } from 'lib/customAttributes';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import {
  Datepicker,
  FormGroup,
  MultipleAttachments,
  Select,
  TextArea,
  WorkloadSettings
} from 'components/common/form';

import type { IndividualAchievement } from 'shared-types';

export type IndividualAchievementFormValues = Pick<
  IndividualAchievement,
  'attachments' | 'comments' | 'customAttributes' | 'title' | 'workload'
> & {
  completionDate: number;
  expirationDate: number;
  startedAtDate: number;
  emails?: string;
  userExpirationDate?: Date | null;
  usernames?: string;
};

interface Props {
  getUploadAttachmentsHandler: (fn: () => void) => void;
  isFutureAchievement?: boolean;
  visible: boolean;
}

const AchievementsIndividualForm = ({
  getUploadAttachmentsHandler,
  isFutureAchievement = false,
  visible
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig } = useReduxState();

  const getTitleOptionsTimeout = useRef<NodeJS.Timeout>(undefined);
  const uploaderHandler = useRef<() => void>(undefined);

  const formApi = useFormApi<IndividualAchievementFormValues>();
  const { errors, values } = useFormState<IndividualAchievementFormValues>();

  const [completionDate, setCompletionDate] = useState<number>(null);
  const [expirationDate, setExpirationDate] = useState<number>(null);
  const [startedAtDate, setStartedAtDate] = useState<number>(null);

  useEffect(() => {
    if (uploaderHandler.current) {
      getUploadAttachmentsHandler(uploaderHandler.current);
    }
  }, [uploaderHandler.current]);

  useEffect(() => {
    if (visible && !values?.completionDate) {
      const initialCompletionDate = isFutureAchievement
        ? addDays(Date.now(), 1)
        : startOfDay(Date.now());
      formApi.setValue('completionDate', initialCompletionDate.getTime());
    }
  }, [visible]);

  useEffect(() => {
    const { values } = formApi?.getState() || {};
    if (values) {
      setCompletionDate(values.completionDate || null);
      setExpirationDate(values.expirationDate || null);
      setStartedAtDate(values.startedAtDate || null);
    }
  }, []);

  const customAttributes =
    availableCustomAttributesByType({
      serverConfig,
      type: 'individualAchievement'
    }) || [];

  const getTitleOptions = (value: string) => {
    clearTimeout(getTitleOptionsTimeout.current);

    return new Promise((resolve) => {
      getTitleOptionsTimeout.current = setTimeout(async () => {
        try {
          const response = await api.get(
            `/admin/achievements-individual?titleSearchOnly=${value}`
          );
          if (response.data && response.data.success) {
            const achievements = response.data.individualAchievements.map(
              (achievement) => ({
                value: achievement.title,
                label: achievement.title
              })
            );
            resolve(achievements);
          } else {
            resolve([]);
          }
        } catch {
          resolve([]);
        }
      }, 500);
    });
  };

  const handleDateChange = (
    value: number,
    field: 'completion' | 'expiration' | 'startedAt'
  ) => {
    if (field === 'startedAt') setStartedAtDate(value);
    else if (field === 'completion') setCompletionDate(value);
    else if (field === 'expiration') setExpirationDate(value);
  };

  const validateCompletionDate = (value: string) => {
    if (!visible) return;
    return value ? undefined : messages.commonFieldRequired;
  };

  const validateTitle = (value: string) => {
    return value?.trim() ? undefined : messages.commonFieldRequired;
  };

  const Menu = (props) => {
    if (!props.options?.length) return null;
    return <components.Menu {...props} />;
  };

  const baseDate = useMemo(
    () =>
      isFutureAchievement
        ? { max: null, min: addDays(startOfDay(dateNow), 1).getTime() }
        : { max: startOfDay(dateNow).getTime(), min: null },
    [dateNow, isFutureAchievement]
  );

  return (
    <>
      {visible ? (
        <FormGroup htmlFor="title" label={messages.achievementTitle}>
          <Select
            async={true}
            components={{ Menu }}
            creatable={true}
            error={errors?.title}
            field="title"
            id="title"
            inputValue={values?.title}
            isRequired={true}
            isValidNewOption={() => false}
            loadingMessage={messages.settingsSearching}
            loadOptions={getTitleOptions}
            updateValueOnInputChange={true}
            value={{ label: values?.title || '', value: values?.title || '' }}
            validate={validateTitle}
            validateOnChange={true}
          />
        </FormGroup>
      ) : null}

      <div className="datepicker-container">
        <FormGroup
          htmlFor="startedAtDate"
          label={messages.commonStartDate}
          labelIsOptional={true}
        >
          <div className="date-picker">
            <Datepicker
              field="startedAtDate"
              id="startedAtDate"
              isClearable={true}
              maxDate={Math.min(
                completionDate,
                ...(baseDate.max ? [baseDate.max] : [])
              )}
              onChange={(value: number) => handleDateChange(value, 'startedAt')}
              simpleClear={true}
              usePortal={true}
            />
          </div>
        </FormGroup>

        <FormGroup
          htmlFor="completionDate"
          label={messages.completionsCompletionDate}
        >
          <div className="date-picker">
            <Datepicker
              field="completionDate"
              id="completionDate"
              minDate={Math.max(
                baseDate.min,
                ...(startedAtDate ? [startedAtDate] : [])
              )}
              maxDate={Math.min(
                baseDate.max,
                ...(expirationDate ? [expirationDate] : [])
              )}
              onChange={(value: number) =>
                handleDateChange(value, 'completion')
              }
              usePortal={true}
              validate={validateCompletionDate}
              validateOnChange={true}
            />
          </div>
        </FormGroup>

        <FormGroup
          htmlFor="expirationDate"
          label={messages.commonExpirationDate}
          labelIsOptional={true}
        >
          <div className="date-picker">
            <Datepicker
              field="expirationDate"
              id="expirationDate"
              isClearable={true}
              minDate={completionDate}
              onChange={(value: number) =>
                handleDateChange(value, 'expiration')
              }
              simpleClear={true}
              usePortal={true}
            />
          </div>
        </FormGroup>
      </div>

      <WorkloadSettings
        fieldWorkloadEnabled="workloadEnabled"
        fieldWorkloadValue="workload"
        workloadEnabledProps={{ label: messages.achievementWorkload }}
        workloadValueProps={{
          label: messages.achievementWorkloadTime,
          stepValue: 1
        }}
      />

      <FormGroup htmlFor="comments" label={messages.commonComment}>
        <TextArea autoGrow={true} id="comments" field="comments" rows={3} />
      </FormGroup>

      <MultipleAttachments
        getUploadHandler={(fn) => (uploaderHandler.current = fn)}
      />

      {visible && customAttributes && (
        <FormGroup htmlFor="customAttributes">
          <ProductCustomAttributes type="individualAchievement" />
        </FormGroup>
      )}

      <style jsx>{`
        .datepicker-container {
          margin-top: 15px;
        }
        @media (min-width: ${breakpoints.medium + 200}px) {
          .date-picker {
            width: 270px;
          }
          .datepicker-container {
            align-items: baseline;
            display: flex;
            gap: 15px;
            justify-content: space-between;
            margin-top: 0px;
          }
        }
      `}</style>
    </>
  );
};

export default AchievementsIndividualForm;
